import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { Dictation, Note } from '../_interfaces/dictation';
import { AuthService } from './auth.service';
import { ProcessingJobState } from '../_interfaces/processing-job';
import { mapNoteSection } from '../_helpers/note-sections';
import { convertNoteSections } from '../_helpers/markdown-to-html';
import { CopilotDocument } from '../_interfaces/document';
import { Template, TemplateType } from '../_interfaces/template';
import { getNextDocumentPosition } from '../_helpers/utils';
import { TranslateService } from '@ngx-translate/core';
import { SentryService } from './error-reporter.service';
import {
  getDictationTranscription,
  GenerationUtterance,
  mapUtterancesToGenerationContent,
} from '../_helpers/transcription';
import {
  hasNoteGenerationSettings,
  resolveGenerationConfig,
  storedGenerationFieldsFromResponse,
} from '../_helpers/note-generation';
import { NoteGenerationSettings } from '../_interfaces/note-generation.interface';

export class SmartDictationStaleError extends Error {
  constructor() {
    super('Smart dictation response discarded');
    this.name = 'SmartDictationStaleError';
  }
}

@Injectable({ providedIn: 'root' })
export class CopilotProcessingService {
  private jobs = new Map<string, ProcessingJobState>();
  private jobs$ = new BehaviorSubject<ProcessingJobState[]>([]);
  private documentsUpdated$ = new ReplaySubject<{ dictationId: string; copilotDocument: CopilotDocument }>(100);
  private templateNotFound$ = new Subject<{ dictationId: string; templateId: number; templateType: string; templateTitle: string }>();
  private processingError$ = new Subject<{ dictationId: string; templateId: number; templateType: string; templateTitle: string; message: string }>();
  private persistQueues = new Map<string, Promise<void>>();

  // Track deleted documents to prevent them from reappearing via ReplaySubject replays
  private deletedDocuments = new Set<string>();
  private dictations = new Map<string, Dictation>(); // Store dictations for offline access

  constructor(private api: ApiService, private auth: AuthService, private translate: TranslateService, private sentry: SentryService) { }

  get processing$() { return this.jobs$.asObservable(); }
  get documentsUpdates$() { return this.documentsUpdated$.asObservable(); }
  get templateNotFoundError$() { return this.templateNotFound$.asObservable(); }
  get processingError$$() { return this.processingError$.asObservable(); }

  /**
   * Mark a document as deleted to prevent it from reappearing via ReplaySubject replays.
   * This is necessary because the ReplaySubject buffers old updates that were emitted
   * before the document was marked with _delete: true.
   */
  markDocumentDeleted(dictationId: string, templateId: number, templateType: string): void {
    const key = this.keyFor(dictationId, templateId, templateType);
    this.deletedDocuments.add(key);
  }

  /**
   * Check if a document has been marked as deleted.
   */
  isDocumentDeleted(dictationId: string, templateId: number, templateType: string): boolean {
    const key = this.keyFor(dictationId, templateId, templateType);
    return this.deletedDocuments.has(key);
  }

  /**
   * Clear the deleted mark for a document (used when re-adding a previously deleted document).
   */
  clearDocumentDeleted(dictationId: string, templateId: number, templateType: string): void {
    const key = this.keyFor(dictationId, templateId, templateType);
    this.deletedDocuments.delete(key);
  }

  /**
   * Get a dictation by ID, either from memory or localStorage
   * This allows access to dictation data even when offline
   */
  getDictation(id: string): Dictation | null {
    // Try memory first
    if (this.dictations.has(id)) {
      return this.dictations.get(id)!;
    }

    // Fall back to localStorage
    try {
      const key = `copilot_dictation_${id}`;
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        const dictation = parsed.dictation as Dictation;
        // Restore to memory
        this.dictations.set(id, dictation);
        return dictation;
      }
    } catch (e) {
      console.error('Failed to retrieve dictation from localStorage:', e);
    }

    return null;
  }

  /**
   * Store dictation locally for offline access
   */
  private storeDictation(dictation: Dictation): void {
    // Store in memory
    this.dictations.set(dictation._id, dictation);

    // Persist to localStorage for page refreshes
    try {
      const key = `copilot_dictation_${dictation._id}`;
      localStorage.setItem(key, JSON.stringify({
        dictation,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Failed to store dictation in localStorage:', e);
    }
  }

  /**
   * Clean up stored dictation after successful completion
   */
  private cleanupDictation(id: string): void {
    this.dictations.delete(id);
    try {
      const key = `copilot_dictation_${id}`;
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Failed to remove dictation from localStorage:', e);
    }
  }

  private findPreviousProcessedDoc(
    dictation: Dictation,
    templateId: number,
    templateType: string,
  ): CopilotDocument | null {
    const list = (dictation.copilot?.documents?.processed ?? []) as CopilotDocument[];
    const isActiveDocForTemplate = (d: CopilotDocument) =>
      d.templateId === templateId && d.templateType === templateType && !d._delete;
    return list.find(isActiveDocForTemplate) ?? null;
  }

  private upsertInPlace(list: CopilotDocument[], doc: CopilotDocument): void {
    const hasSameTemplateIdentity = (d: CopilotDocument) =>
      d.templateId === doc.templateId && d.templateType === doc.templateType;
    const idx = list.findIndex(hasSameTemplateIdentity);
    if (idx >= 0) {
      list[idx] = doc;
      return;
    }
    list.push(doc);
  }

  private ensureDocumentsContainer(dictation: Dictation): { original: CopilotDocument[]; processed: CopilotDocument[] } {
    if (!dictation.copilot) {
      (dictation as any).copilot = { documents: { original: [], processed: [] } };
    }
    const docs = dictation.copilot!.documents;
    docs.original = (docs.original ?? []) as CopilotDocument[];
    docs.processed = (docs.processed ?? []) as CopilotDocument[];
    return docs;
  }

  private buildDocFromJob(
    job: ProcessingJobState,
    sections: Record<string, { title: string; text: string }>,
    error: boolean,
    previous?: CopilotDocument | null,
    completedAt?: string,
    recipients?: CopilotDocument['recipients'],
  ): CopilotDocument {
    const syncTimestamp = completedAt ?? new Date().toISOString();
    return {
      templateId: job.templateId,
      templateType: job.templateType as TemplateType,
      templateTitle: job.templateTitle ?? '',
      sections,
      error,
      rate: 0,
      workflow_trigger: previous?.workflow_trigger ?? !!job.workflow_trigger,
      priority: previous?.priority ?? 1,
      position: previous?.position ?? job.position,
      _delete: false,
      approved: previous?.approved ?? false,
      source_content_updated_at: error
        ? previous?.source_content_updated_at
        : (job.contentUpdatedAtSnapshot ?? previous?.source_content_updated_at),
      last_synced_at: error ? previous?.last_synced_at : syncTimestamp,
      ...((recipients ?? previous?.recipients)
        ? { recipients: recipients ?? previous?.recipients }
        : {}),
    };
  }

  private enqueuePersist<T>(dictationId: string, task: () => Promise<T>): Promise<T> {
    const previous = this.persistQueues.get(dictationId) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(task);
    const tail = next.then(() => undefined, () => undefined);

    this.persistQueues.set(dictationId, tail);
    tail.finally(() => {
      if (this.persistQueues.get(dictationId) === tail) {
        this.persistQueues.delete(dictationId);
      }
    });

    return next;
  }

  private async applyUpsertPersistEmit(dictation: Dictation, doc: CopilotDocument): Promise<void> {
    await this.enqueuePersist(dictation._id, async () => {
      await this.api.updateDictationCopilot(dictation._id, {
        copilot: {
          documents: {
            original: [doc],
            processed: [doc],
          },
        },
      });

      const docs = this.ensureDocumentsContainer(dictation);
      this.upsertInPlace(docs.processed, doc);
      this.upsertInPlace(docs.original, doc);

      this.documentsUpdated$.next({ dictationId: dictation._id, copilotDocument: doc });
    });
  }

  private buildGenerationPayload(
    dictation: Dictation,
    template: { templateId: number; templateType: string; templateTitle?: string },
    content: string | GenerationUtterance[],
  ): Record<string, unknown> {
    const user = this.auth.getUser();
    const subjectDeathIndicator = dictation.copilot?.subject_death_indicator;
    return {
      dictationId: dictation._id,
      templateId: template.templateId,
      templateType: template.templateType,
      templateTitle: template.templateTitle ?? '',
      content,
      speciality: user?.settings?.copilotSpeciality ?? '',
      notes: (dictation.copilot?.notes ?? []).map((item: Note) => item.note),
      patientContext: {
        patientName: dictation.copilot?.subject_name ?? '',
        patientId: dictation.copilot?.subject_id ?? '',
        dateOfBirth: dictation.copilot?.subject_dob ?? '',
        consultationDate: dictation.createdAt ?? '',
        ...(subjectDeathIndicator === 'Y' ? { subject_death_indicator: 'Y' } : {}),
      },
      visit_id: dictation.copilot?.visit_id ?? null,
      order_id: dictation.copilot?.order_id ?? null,
    };
  }

  /**
   * Sync POST /v1/shared/smart — incremental document generation on dictation pause.
   * Does not use copilot-process, ?async, or scribe-jobs polling.
   * `content` stays the incremental text delta, not the full utterance list.
   */
  async processSmartSync(
    dictation: Dictation,
    options: {
      templateId: number;
      templateType: string;
      templateTitle: string;
      content: string;
      currentNote?: string;
      previous?: CopilotDocument | null;
      pauseSeq?: number;
      noteRevision?: number;
      signal?: AbortSignal;
      isStale?: () => boolean;
    },
  ): Promise<CopilotDocument> {
    const previous = options.previous
      ?? this.findPreviousProcessedDoc(dictation, options.templateId, options.templateType)
      ?? null;

    const payload = this.buildGenerationPayload(dictation, options, options.content);
    payload['currentNote'] = options.currentNote ?? '';
    if (options.pauseSeq !== undefined) {
      payload['pauseSeq'] = options.pauseSeq;
    }
    if (options.noteRevision !== undefined) {
      payload['noteRevision'] = options.noteRevision;
    }
    const config = resolveGenerationConfig({ previousDoc: previous });
    if (hasNoteGenerationSettings(config)) {
      payload['config'] = config;
    }

    const response = await this.api.smartProcess(payload, options.signal);
    if (options.signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }
    if (options.isStale?.()) {
      throw new SmartDictationStaleError();
    }

    const echoedPauseSeq = response?.pauseSeq;
    const echoedNoteRevisionIn = response?.noteRevisionIn;
    if (
      (options.pauseSeq !== undefined && echoedPauseSeq != null && echoedPauseSeq < options.pauseSeq)
      || (
        options.noteRevision !== undefined
        && echoedNoteRevisionIn != null
        && echoedNoteRevisionIn !== options.noteRevision
      )
    ) {
      throw new SmartDictationStaleError();
    }
    const rawSections = response?.note?.sections ?? response?.sections;
    const sections = convertNoteSections(mapNoteSection(rawSections), options.templateTitle);

    if (Object.keys(sections).length === 0) {
      throw new Error(this.translate.instant('Something went wrong.'));
    }

    const processedDoc: CopilotDocument = {
      ...this.buildDocFromJob(
        {
          dictationId: dictation._id,
          templateId: options.templateId,
          templateType: options.templateType,
          templateTitle: options.templateTitle ?? '',
          status: 'running',
          action: 'process',
          position: previous?.position ?? 0,
        } as ProcessingJobState,
        sections,
        false,
        previous,
        new Date().toISOString(),
      ),
      ...storedGenerationFieldsFromResponse(
        response,
        previous,
        hasNoteGenerationSettings(config),
      ),
    };

    await this.applyUpsertPersistEmit(dictation, processedDoc);
    return processedDoc;
  }

  /** Enqueue processing for a dictation. If templates provided, process those; otherwise uses dictation.copilot.documents.original. */
  enqueue(
    dictation: Dictation,
    templates: Template[],
    action: 'process' | 'reprocess' | 'update' = 'process',
    options?: { config?: NoteGenerationSettings },
  ): void {
    // Store dictation locally for offline access and retries
    this.storeDictation(dictation);

    const existingDocs = dictation.copilot?.documents?.processed ?? [];
    let nextPosition = getNextDocumentPosition(existingDocs);

    const originals = templates.map(t => ({ templateId: Number(t.id), templateType: t.type, templateTitle: t.title, workflow_trigger: !!t.workflow_trigger }))
    for (const o of originals) {
      const key = this.keyFor(dictation._id, o.templateId, o.templateType);

      // Clear deleted mark if document is being re-added
      this.deletedDocuments.delete(key);

      if (this.jobs.has(key)) continue;
      // Emit a placeholder immediately so the UI can reflect a generating document
      const previous = this.findPreviousProcessedDoc(dictation, o.templateId, o.templateType);
      const position = previous?.position ?? nextPosition++;
      const state: ProcessingJobState = {
        dictationId: dictation._id,
        templateId: o.templateId,
        templateType: o.templateType,
        templateTitle: o.templateTitle,
        workflow_trigger: o.workflow_trigger,
        status: 'queued',
        action: action,
        position,
        // Snapshot existing content so a later failure can never discard it.
        previousDoc: previous ? { ...previous, sections: { ...previous.sections } } : undefined,
        contentUpdatedAtSnapshot: dictation.copilot?.content_updated_at,
        config: resolveGenerationConfig({
          previousDoc: previous,
          config: options?.config,
        }),
      };
      this.jobs.set(key, state);
      if (!previous) {
        const placeholder = this.buildDocFromJob(state, {}, false, null);
        this.documentsUpdated$.next({ dictationId: dictation._id, copilotDocument: placeholder });
      }
      this.emitJobs();
      this.runJob(dictation, state);
    }
  }

  private async resolveGenerationContent(dictation: Dictation): Promise<string | GenerationUtterance[]> {
    const fallback = getDictationTranscription(dictation) || 'Use notes to generate the document.';
    try {
      const utterances = await this.api.getUtterances(dictation._id);
      const content = mapUtterancesToGenerationContent(utterances);
      if (content.length > 0) return content;
    } catch (error) {
      console.error('Error fetching utterances:', error);
    }
    return fallback;
  }

  private async runJob(dictation: Dictation, job: ProcessingJobState): Promise<void> {
    try {
      job.status = 'running';
      job.cancelled = false;
      this.emitJobs();

      const payload: Record<string, unknown> = this.buildGenerationPayload(
        dictation,
        job,
        await this.resolveGenerationContent(dictation),
      );
      payload['workflow_trigger'] = job.workflow_trigger ?? false;

      if (hasNoteGenerationSettings(job.config)) {
        payload['config'] = job.config;
      }

      const submitResponse = await this.api.copilotProcess(payload);

      // Async path: 202 with job_id
      if (submitResponse?.job_id) {
        job.jobId = submitResponse.job_id;
        this.emitJobs();
        const result = await this.pollForResult(job);
        await this.applyJobResult(dictation, job, result);
        return;
      }

      // Sync fallback: direct response with note.sections
      await this.applyJobResult(dictation, job, submitResponse);
    } catch (e: any) {
      this.handleJobError(dictation, job, e);
    }
  }

  private async applyJobResult(dictation: Dictation, job: ProcessingJobState, response: any): Promise<void> {
    const rawSections = response?.note?.sections ?? response?.sections;
    const sections = convertNoteSections(mapNoteSection(rawSections), job.templateTitle);
    const previous = job.previousDoc
      ?? this.findPreviousProcessedDoc(dictation, job.templateId, job.templateType)
      ?? null;

    // An empty result is effectively a failed generation. Never discard previously
    // generated content in that case: keep what we already had.
    const resultIsEmpty = Object.keys(sections).length === 0;
    const previousHasContent = !!previous?.sections && Object.keys(previous.sections).length > 0;
    if (resultIsEmpty && previousHasContent) {
      const preservedDoc: CopilotDocument = { ...previous!, error: false };
      const docs = this.ensureDocumentsContainer(dictation);
      this.upsertInPlace(docs.processed, preservedDoc);
      this.upsertInPlace(docs.original, preservedDoc);
      this.documentsUpdated$.next({ dictationId: dictation._id, copilotDocument: preservedDoc });
      job.status = 'failed';
      this.emitJobs();
      // An empty result is a failed regeneration: keep content but surface an error banner.
      this.emitProcessingError(dictation._id, job.templateId, job.templateType, job.templateTitle);
      return;
    }

    const processedDoc: CopilotDocument = {
      ...this.buildDocFromJob(job, sections, false, previous, new Date().toISOString(), response?.recipients),
      ...storedGenerationFieldsFromResponse(
        response,
        previous,
        hasNoteGenerationSettings(job.config),
      ),
    };

    await this.applyUpsertPersistEmit(dictation, processedDoc);

    job.status = 'completed';
    this.emitJobs();

    const hasOtherJobs = Array.from(this.jobs.values()).some(
      j => j.dictationId === dictation._id && j.status !== 'completed'
    );
    if (!hasOtherJobs) {
      this.cleanupDictation(dictation._id);
    }
  }

  private async pollForResult(job: ProcessingJobState): Promise<any> {
    const MAX_ATTEMPTS = 90;
    const INITIAL_DELAY_MS = 10_000;
    const BACKOFF_BASE_MS = 2_000;
    const MAX_DELAY_MS = 5_000;
    const BACKOFF_FACTOR = 1.5;

    await this.sleep(INITIAL_DELAY_MS);

    let delay = BACKOFF_BASE_MS;
    let consecutiveFailedCount = 0;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (job.cancelled) {
        throw new Error('Job cancelled');
      }

      const data = await this.api.pollScribeJob(job.jobId!);

      if (data.status === 'completed' && data.result) {
        return data.result;
      }

      if (data.status === 'failed') {
        consecutiveFailedCount++;
        if (consecutiveFailedCount >= 3) {
          throw new Error(data.failed_reason || 'Job processing failed after multiple attempts');
        }
      } else {
        consecutiveFailedCount = 0;
      }

      await this.sleep(delay);
      delay = Math.min(delay * BACKOFF_FACTOR, MAX_DELAY_MS);
    }

    throw new Error('Document generation timed out. Please try again.');
  }

  private handleJobError(dictation: Dictation, job: ProcessingJobState, e: any): void {
    const isNetworkError = !navigator.onLine ||
      e?.status === 0 ||
      e?.name === 'HttpErrorResponse' && e?.status === 0 ||
      e?.message?.toLowerCase().includes('network') ||
      e?.message?.toLowerCase().includes('failed to fetch') ||
      e?.message?.toLowerCase().includes('networkerror');

    const errorMessage = (e?.message ?? '').toString().toLowerCase();
    const nestedErrorMessage = (e?.error?.message ?? '').toString().toLowerCase();
    const isTemplateNotFound = e?.status === 404 ||
      e?.error?.statusCode === 404 ||
      nestedErrorMessage.includes('template not found') ||
      errorMessage.includes('template not found');

    job.status = 'failed';

    if (isNetworkError) {
      job.error = 'No internet connection. Document will be generated when connectivity is restored.';
      this.emitProcessingError(dictation._id, job.templateId, job.templateType, job.templateTitle, job.error);
    } else if (isTemplateNotFound) {
      job.error = this.translate.instant('Blueprint not found');
      this.templateNotFound$.next({
        dictationId: dictation._id,
        templateId: job.templateId,
        templateType: job.templateType,
        templateTitle: job.templateTitle ?? ''
      });
    } else {
      job.error = e?.message ?? this.translate.instant('Failed to process document');
      this.sentry.captureException(e instanceof Error ? e : new Error(job.error), {
        context: 'note_generation',
        dictationId: dictation._id,
        templateId: job.templateId,
        templateTitle: job.templateTitle ?? '',
      });
      this.emitProcessingError(dictation._id, job.templateId, job.templateType, job.templateTitle, job.error);
    }

    // Resolve the content that existed before this job ran. Prefer the snapshot
    // captured when the job was queued so it survives any in-flight state changes.
    const previous = job.previousDoc
      ?? this.findPreviousProcessedDoc(dictation, job.templateId, job.templateType)
      ?? null;
    const hasPreviousContent = !!previous?.sections && Object.keys(previous.sections).length > 0;

    // RULE: a failed job must NEVER discard previously generated content, no matter
    // the error type (404, 500, timeout, network, ...). If we have prior content, keep
    // it on screen and leave the backend copy untouched.
    if (hasPreviousContent) {
      const preservedDoc: CopilotDocument = { ...previous!, error: false };
      const docs = this.ensureDocumentsContainer(dictation);
      this.upsertInPlace(docs.processed, preservedDoc);
      this.upsertInPlace(docs.original, preservedDoc);
      this.storeDictation(dictation);
      this.documentsUpdated$.next({ dictationId: dictation._id, copilotDocument: preservedDoc });
      this.emitJobs();

      if (isNetworkError && !navigator.onLine) {
        this.scheduleReconnectRetry(dictation, job);
      }
      return;
    }

    // No prior content exists (first-time generation failure): surface the error
    // state so the user can retry, since there is nothing to preserve.
    const failedDoc = this.buildDocFromJob(job, {}, true, previous);

    if (isNetworkError) {
      const docs = this.ensureDocumentsContainer(dictation);
      this.upsertInPlace(docs.processed, failedDoc);
      this.upsertInPlace(docs.original, failedDoc);
      this.storeDictation(dictation);
      this.documentsUpdated$.next({ dictationId: dictation._id, copilotDocument: failedDoc });
      this.emitJobs();

      if (!navigator.onLine) {
        this.scheduleReconnectRetry(dictation, job);
      }
      return;
    }

    try {
      this.applyUpsertPersistEmit(dictation, failedDoc);
    } catch (_) {
      this.documentsUpdated$.next({ dictationId: dictation._id, copilotDocument: failedDoc });
    }
    this.emitJobs();
  }

  private emitProcessingError(
    dictationId: string,
    templateId: number,
    templateType: string,
    templateTitle: string | undefined,
    message?: string
  ): void {
    this.processingError$.next({
      dictationId,
      templateId,
      templateType,
      templateTitle: templateTitle ?? '',
      message: message ?? this.translate.instant('Failed to process document'),
    });
  }

  private scheduleReconnectRetry(dictation: Dictation, job: ProcessingJobState): void {
    const onlineHandler = () => {
      window.removeEventListener('online', onlineHandler);
      const latestDictation = this.getDictation(dictation._id);
      if (latestDictation && job.status === 'failed') {
        setTimeout(() => {
          this.runJob(latestDictation, job);
        }, 1000);
      }
    };
    window.addEventListener('online', onlineHandler);
  }

  /**
   * Recover completed jobs from the backend after session disruption.
   * The proxy derives the user from the auth token — no params needed.
   */
  async recoverOrphanedJobs(): Promise<void> {
    try {
      const response = await this.api.getScribeJobs();
      const jobs: any[] = response?.jobs ?? [];

      for (const job of jobs) {
        const recoveredSections = job.result?.note?.sections ?? job.result?.sections;
        if (job.status === 'completed' && recoveredSections) {
          const sections = convertNoteSections(
            mapNoteSection(recoveredSections),
            job.result?.templateTitle,
          );

          const templateId = job.result.templateId ?? 0;
          const templateType = job.result.templateType ?? 'default';
          const templateTitle = job.result.templateTitle ?? '';

          const recoveredDoc: CopilotDocument = {
            templateId,
            templateType,
            templateTitle,
            sections,
            error: false,
            rate: 0,
            workflow_trigger: false,
            priority: 1,
            _delete: false,
            approved: false,
            ...(job.result?.recipients ? { recipients: job.result.recipients } : {}),
            ...storedGenerationFieldsFromResponse(job.result),
          };

          const dictationId = job.result.dictationId ?? job.result.session_identifier ?? '';
          if (dictationId) {
            this.documentsUpdated$.next({ dictationId, copilotDocument: recoveredDoc });
          }
        }
      }
    } catch (e) {
      console.error('Failed to recover orphaned jobs:', e);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private keyFor(dictationId: string, templateId: number, templateType: string): string {
    return `${dictationId}::${templateId}::${templateType}`;
  }

  private emitJobs(): void {
    const list = Array.from(this.jobs.values());
    this.jobs$.next(list);
    // Cleanup completed/failed to avoid memory growth
    for (const j of list) {
      if (j.status === 'completed' || j.status === 'failed') {
        const key = this.keyFor(j.dictationId, j.templateId, j.templateType);
        this.jobs.delete(key);
      }
    }
  }

}
